/**
 * Badges for regulatory flags (PII, ITAR, EAR, HIPAA, GDPR, etc.)
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface RegulatoryFlagBadgeProps {
  readonly flag: string
  readonly className?: string
}

const flagConfig: Record<string, { className: string }> = {
  PII: {
    className: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
  },
  ITAR: {
    className: 'border-red-600/30 bg-red-600/10 text-red-700 dark:text-red-400',
  },
  EAR: {
    className:
      'border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-400',
  },
  HIPAA: {
    className:
      'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
  },
  GDPR: {
    className:
      'border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-400',
  },
  CUI: {
    className:
      'border-yellow-600/30 bg-yellow-600/10 text-yellow-700 dark:text-yellow-400',
  },
}

const defaultFlagConfig = {
  className: 'border-muted-foreground/30 text-muted-foreground',
}

export function RegulatoryFlagBadge({
  flag,
  className,
}: RegulatoryFlagBadgeProps) {
  const config = flagConfig[flag] ?? defaultFlagConfig

  return (
    <Badge variant="outline" className={cn('text-xs', config.className, className)}>
      {flag}
    </Badge>
  )
}

interface RegulatoryFlagListProps {
  readonly flags: readonly string[]
  readonly className?: string
}

export function RegulatoryFlagList({
  flags,
  className,
}: RegulatoryFlagListProps) {
  if (flags.length === 0) {
    return <span className="text-xs text-muted-foreground">None</span>
  }

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {flags.map((flag) => (
        <RegulatoryFlagBadge key={flag} flag={flag} />
      ))}
    </div>
  )
}
